import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverKYC() {
  const { api, AdminId } = useGlobalContext();

  const [kycDrivers, setKycDrivers] = useState([]);
  const [kycStatusLoading, setKycStatusLoading] = useState(false);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  /* ================= FETCH ALL DRIVER KYC ================= */

  const fetchDriverKycDetails = useCallback(
    async (preserveLocalUpdates = true) => {
      setLoading(true);

      try {
        const res = await api.post("/api/Driver/v1.0/getAllDriverKycDetails", {
          userid: "",
          adminId: AdminId,
        });

        if (res.data?.responseCode === "00") {
          const serverData = Array.isArray(res.data?.data)
            ? res.data.data
            : [];

          if (preserveLocalUpdates) {
            setKycDrivers((prev) =>
              serverData.map((serverDriver) => {
                const local = (prev || []).find(
                  (driver) =>
                    driver.id === serverDriver.id ||
                    driver.driver_code === serverDriver.driver_code
                );

                if (
                  local &&
                  ["Approved", "Rejected"].includes(local.status) &&
                  serverDriver.status === "Pending"
                ) {
                  return {
                    ...serverDriver,
                    status: local.status,
                  };
                }

                return serverDriver;
              })
            );
          } else {
            setKycDrivers(serverData);
          }

          return serverData;
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(
            res.data?.responseMessage || "Failed to fetch Driver KYC details"
          );
        }

        setKycDrivers([]);
        return [];
      } catch (err) {
        console.error("fetchDriverKycDetails error:", err);
        toast.error("Error fetching Driver KYC details");
        setKycDrivers([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [api, AdminId]
  );

  /* ================= VIEW / UPDATE KYC ================= */

  const handleKycAction = useCallback(
    async (payload) => {
      if (!payload) {
        toast.error("KYC payload is required");
        return null;
      }

      setKycStatusLoading(true);

      try {
        const res = await api.post(
          "/api/Driver/v1.0/verifyDriverkycDetails",
          payload
        );

        if (res.data?.responseCode === "00") {
          toast.success(res.data?.responseMessage || "KYC updated");
          return res.data;
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(res.data?.responseMessage || "KYC update failed");
        }

        return null;
      } catch (err) {
        console.error("handleKycAction error:", err);
        toast.error(err?.message || "KYC update failed");
        return null;
      } finally {
        setKycStatusLoading(false);
      }
    },
    [api]
  );

  /* ================= SUBMIT KYC STEP ================= */

  const handleStepSubmit = async (stepNumber, stepData) => {
    if (!stepNumber || !stepData) {
      toast.error("KYC step data is required");
      return false;
    }

    try {
      setKycSubmitting(true);

      const payload = {
        adminId: AdminId,
        userid: stepData.userid,
        step: stepNumber.toString(),

        step1: stepData.step1 || {},
        step2: stepData.step2 || {},
        step3: stepData.step3 || {},
        step4: stepData.step4 || {},
        step5: stepData.step5 || {},
        step6: stepData.step6 || {},

        p1: "",
        p2: "",
        p3: "",
        p4: "",
        p5: "",
      };

      const res = await api.post(
        "/api/Driver/v2.0/addDriverKYCDetails",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || `Step ${stepNumber} saved successfully`
        );
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to save step");
      }

      return false;
    } catch (err) {
      console.error("handleStepSubmit error:", err);
      toast.error("Error saving KYC data");
      return false;
    } finally {
      setKycSubmitting(false);
    }
  };

  return {
    kycDrivers,
    loading,
    fetchDriverKycDetails,

    kycStatusLoading,
    handleKycAction,

    kycSubmitting,
    handleStepSubmit,
  };
}